import { type ReactNode, Suspense, lazy, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAsset, useDeleteAsset } from "../api/assetlib";
import { ApiError } from "../api/client";
import type { AssetDetail, AssetKind } from "../api/types";
import { CAST } from "../copy/cast";
import { ErrorLine } from "../ui/ErrorLine";
import { formatRelative } from "../ui/format";
import { useNow } from "../ui/useNow";
import { NotFoundPage } from "../pages/NotFoundPage";
import { RequireFeature } from "../shell/RequireFeature";
import { Button, Card, PageHeader, Pill, Skeleton } from "../ui";
import { VIEWER_COPY } from "../viewer/Frame";
import { Breadcrumb } from "./Breadcrumb";
import { EditDetailsDialog } from "./EditDetailsDialog";
import { SectionHeader } from "./SectionHeader";
import { Star } from "./Star";
import { deleteConfirmText } from "./deleteConfirm";
import { licenceLine } from "./licence";

const loadViewer = () => import("../viewer/Viewer");
let viewerAsked = false;

/** Asks for the 3D view's code now, once, without waiting for anything that
 *  will draw it. An inspector route calls it as it is entered, and a card of
 *  the grid when the pointer or the focus reaches it: the code then travels
 *  beside `/v1/me` and the asset's detail instead of after them. It is still
 *  a chunk of its own (`scripts/check-bundle.mjs`). A failed download is
 *  forgotten, so `Viewer` asks again when it is drawn. */
export function preloadViewer(): void {
  if (viewerAsked) return;
  viewerAsked = true;
  loadViewer().catch(() => {
    viewerAsked = false;
  });
}

/** The 3D view, loaded only when an inspector page is opened or about to be
 *  (`preloadViewer`): the rest of the app does not pay for three
 *  (`scripts/check-bundle.mjs`). */
export const Viewer = lazy(loadViewer);

/** What an inspector route renders: the feature's gate, as on every route of
 *  the 3D library, and the 3D view's code asked for at once. The gate waits
 *  for `/v1/me`; the code does not. */
export function InspectorRoute({ children }: { children: ReactNode }) {
  useEffect(preloadViewer, []);
  return <RequireFeature name="assetlib">{children}</RequireFeature>;
}

/** The square the 3D view occupies, with a skeleton until its code arrives. */
export function ViewerSlot({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="viewer">
          <div className="viewer-box">
            <Skeleton width="100%" height="100%" />
          </div>
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

/** Viewer beside the panel from 1024 px, above it on narrower screens. */
export const INSPECTOR_GRID = "grid items-start gap-6 lg:grid-cols-2";

const EYEBROW = "— Objects";

function Loading() {
  return (
    <>
      <PageHeader eyebrow={EYEBROW} />
      <SectionHeader />
      <div className={INSPECTOR_GRID} aria-busy="true">
        <div className="viewer">
          <div className="viewer-box">
            <Skeleton width="100%" height="100%" />
            <p role="status" className="viewer-status">
              {VIEWER_COPY.loading}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-3" data-testid="inspector-skeleton">
          <Skeleton width="100%" height={44} />
          <Skeleton width="100%" height={44} />
          <Skeleton width="100%" height={44} />
        </div>
      </div>
    </>
  );
}

type Asset = ReturnType<typeof useAsset>;

/** What every inspector page does before it has something to show: the
 *  skeleton, the not-found page for an id the organisation cannot see, a
 *  sentence for any other failure. `children` draws the page; it is mounted
 *  afresh for each asset, so a page's own state starts with its detail. */
export function Inspector(props: {
  kind: AssetKind;
  id: string;
  children(detail: AssetDetail, asset: Asset): ReactNode;
}) {
  const { kind, id } = props;
  const asset = useAsset(kind, id);
  // Gone (deleted here or elsewhere) is gone, whatever is still in hand.
  if (asset.error instanceof ApiError && asset.error.status === 404) {
    return <NotFoundPage />;
  }
  // A detail in hand stays on screen even when asking for fresh addresses
  // failed: the user's work on the page is worth more than the error.
  if (asset.data) return <>{props.children(asset.data, asset)}</>;
  if (asset.isPending) return <Loading />;
  return (
    <>
      <PageHeader eyebrow={EYEBROW} />
      <SectionHeader />
      <ErrorLine error={asset.error} context="plain" />
    </>
  );
}

/** **Edit details** and **Delete**, for one of the organisation's own. */
function OwnActions(props: {
  detail: AssetDetail;
  onEdit(): void;
  onDeleting?: () => void;
  onFailure(error: unknown): void;
}) {
  const { detail } = props;
  const navigate = useNavigate();
  const remove = useDeleteAsset();
  const onDelete = () => {
    if (remove.isPending) return;
    if (!window.confirm(deleteConfirmText(detail))) return;
    props.onFailure(null);
    remove.mutate(
      { kind: detail.kind, id: detail.id },
      {
        onSuccess: () => {
          // The page is left without its unsaved-changes question.
          props.onDeleting?.();
          navigate("/assets?tier=org", { state: { deleted: true } });
        },
        onError: props.onFailure,
      },
    );
  };
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <Button
        type="button"
        size="sm"
        className="min-h-[44px] sm:min-h-0"
        onClick={props.onEdit}
      >
        Edit details
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="min-h-[44px] sm:min-h-0"
        disabled={remove.isPending}
        onClick={onDelete}
      >
        Delete
      </Button>
    </div>
  );
}

/** What an object's page says to someone who has just added it. */
export const JUST_ADDED =
  "In Objects. Dress it and save a look, or press Use in a new ad.";

/** The top of an inspector page: the name with its pills, the section's
 *  header, the breadcrumb, then the description, the tags and where the
 *  thing comes from. An id is never shown. One of the organisation's own
 *  also has **Edit details** and **Delete**; `onDeleting` is called just
 *  before the page is left after a delete. */
export function InspectorHeader({
  detail,
  onDeleting,
}: {
  detail: AssetDetail;
  onDeleting?: () => void;
}) {
  const location = useLocation();
  const arrival = location.state as { saved?: boolean; added?: boolean } | null;
  const saved = arrival?.saved === true;
  // Arrived from adding a model (or, later, a design) to the library.
  const added = arrival?.added === true;
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const meta = detail.meta;
  const name = String(meta.name ?? "");
  const shapeName = detail.shape ? String(detail.shape.meta.name ?? "") : "";
  const description = String(meta.description ?? "").trim();
  const tags: string[] = Array.isArray(meta.tags) ? meta.tags : [];
  const licence = licenceLine(meta.license);
  // The star stands with the pills, under the name: the heading itself stays
  // the name and nothing else.
  const pills = (
    <>
      {detail.tier === "org" ? (
        <Pill size="sm" tone="purple">
          Yours
        </Pill>
      ) : null}
      {detail.shape ? (
        <Link
          to={`/assets/shape/${detail.shape.id}`}
          className="inline-flex min-h-[44px] items-center sm:min-h-0"
        >
          <Pill size="sm">Look of {shapeName}</Pill>
        </Link>
      ) : null}
      <Star kind={detail.kind} id={detail.id} on={detail.favorite} />
    </>
  );
  return (
    <>
      <PageHeader
        eyebrow={EYEBROW}
        title={name}
        meta={pills}
        right={
          detail.can_delete ? (
            <OwnActions
              detail={detail}
              onEdit={() => {
                setEdited(false);
                setEditing(true);
              }}
              onDeleting={onDeleting}
              onFailure={setFailure}
            />
          ) : undefined
        }
      />
      <SectionHeader />
      <Breadcrumb kind={detail.kind} name={name} />
      {detail.can_delete ? (
        <EditDetailsDialog
          open={editing}
          onClose={() => setEditing(false)}
          detail={detail}
          onSaved={() => {
            setEditing(false);
            setEdited(true);
          }}
        />
      ) : null}
      {failure ? (
        <div className="mb-4">
          <ErrorLine error={failure} context="asset_delete" />
        </div>
      ) : null}
      {edited ? (
        <p role="status" className="mb-4 mt-0 text-sm text-slate-700">
          Saved.
        </p>
      ) : saved ? (
        <p role="status" className="mb-4 mt-0 text-sm text-slate-700">
          Saved to Objects.
        </p>
      ) : added ? (
        <p role="status" className="mb-4 mt-0 text-sm text-slate-700">
          {JUST_ADDED}
        </p>
      ) : null}
      {detail.kind === "variant" && detail.stale ? (
        <p role="status" className="mb-4 mt-0 text-sm text-slate-700">
          This look no longer fits {shapeName}.
        </p>
      ) : null}
      <div className="mb-6 flex flex-col gap-2 text-sm text-slate-700">
        {description ? (
          <p className="m-0" data-testid="asset-description">
            {description}
          </p>
        ) : null}
        {tags.length > 0 ? (
          <ul
            className="m-0 flex list-none flex-wrap gap-x-3 gap-y-1 p-0"
            aria-label="Tags"
          >
            {tags.map((tag) => (
              <li key={tag}>
                <Link
                  to={`/assets?q=${encodeURIComponent(tag)}`}
                  className="inline-flex min-h-[44px] items-center underline sm:min-h-0"
                >
                  {tag}
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
        {detail.made_for?.length ? <MadeFor madeFor={detail.made_for} /> : null}
        {licence ? (
          <p className="m-0 text-slate-600" data-testid="asset-licence">
            {licence}
          </p>
        ) : null}
      </div>
    </>
  );
}

/** "Made for {ad titles}" (docs/SPEC.md §6.4): the ads a design was made
 *  for through the studio's **Use in {ad}**, each a link to its ad. */
function MadeFor({ madeFor }: { madeFor: { ad_id: string; title: string }[] }) {
  const [before] = CAST.madeForObjects.split("{titles}");
  return (
    <p className="m-0 text-slate-700" data-testid="asset-made-for">
      {before}
      {madeFor.map((m, k) => (
        <span key={m.ad_id}>
          {k === 0 ? "" : k === madeFor.length - 1 ? " and " : ", "}
          <Link
            to={`/ads/${encodeURIComponent(m.ad_id)}`}
            className="py-2 underline sm:py-0"
          >
            {m.title}
          </Link>
        </span>
      ))}
    </p>
  );
}

/** "Used in your ads": the ten newest, and how many more there are. */
export function UsedInAds({ detail }: { detail: AssetDetail }) {
  const now = useNow(60_000);
  const more = detail.used_in_count - detail.used_in.length;
  return (
    <Card className="mt-6 flex flex-col gap-2 p-6" data-testid="used-in">
      <h2 className="tb-h-row m-0">Used in your ads</h2>
      {detail.used_in.length === 0 ? (
        <p className="m-0 text-sm text-slate-700">
          Not used in any of your ads yet.
        </p>
      ) : (
        <>
          <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm text-slate-700">
            {detail.used_in.map((use) => (
              <li
                key={use.ad_id}
                className="flex flex-wrap items-center gap-x-2"
              >
                <Link
                  to={`/ads/${encodeURIComponent(use.ad_id)}`}
                  className="inline-flex min-h-[44px] items-center underline sm:min-h-0"
                >
                  {use.title}
                </Link>
                <span className="text-slate-500">
                  {formatRelative(use.created_at, now)}
                </span>
              </li>
            ))}
          </ul>
          {more > 0 ? (
            <p className="m-0 text-sm text-slate-600">
              and {more.toLocaleString("en-US")} more
            </p>
          ) : null}
        </>
      )}
    </Card>
  );
}

/** The objects a surface or an artwork can be tried on: each opens the
 *  object wearing it. */
export function TryOnCards(props: {
  items: NonNullable<AssetDetail["try_on"]>;
  to(item: NonNullable<AssetDetail["try_on"]>[number]): string;
}) {
  return (
    <ul
      className="m-0 grid list-none grid-cols-2 gap-4 p-0 sm:grid-cols-4"
      data-testid="try-on"
    >
      {props.items.map((item) => (
        <li key={item.id}>
          <Link to={props.to(item)} className="block text-left no-underline">
            <span className="block aspect-square w-full overflow-hidden rounded-lg bg-slate-100">
              {item.thumb_url ? (
                <img
                  src={item.thumb_url}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              ) : null}
            </span>
            <span className="mt-2 block break-words text-sm font-semibold text-slate-800">
              {item.name}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
